import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowRight, ArrowLeft, Check, User, GraduationCap, MapPin, Heart } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
const DIETS = ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan', 'Jain'];
const INCOMES = ['Below 3 Lakh', '3-5 Lakh', '5-8 Lakh', '8-12 Lakh', '12-20 Lakh', '20-30 Lakh', '30-50 Lakh', '50 Lakh+'];
const COMPLEXIONS = ['Very Fair', 'Fair', 'Wheatish', 'Dark'];
const FAMILY_TYPES = ['Nuclear', 'Joint', 'Extended'];
const DEGREES = ['High School', 'Diploma', 'Bachelors', 'Masters', 'PhD', 'Professional (CA/CS/MBBS/LLB)'];

export default function RegisterPage() {
  const { t } = useLanguage();
  const { setUser, setNeedsRegistration, getFirebaseToken } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    profile_for: 'self', name: '', gender: 'male', dob: '', birth_time: '', birth_place: '',
    religion: '', caste: '', sub_caste: '',
    height: '', weight: '', complexion: '', education_degree: '', education_field: '', education_college: '',
    occupation: '', company: '', income: '', family_type: '', father_occupation: '', mother_occupation: '',
    diet: '', smoking: 'no', drinking: 'no', hobbies: '', city: '', state: '', country: 'India',
    native_place: '', bio: '', preferred_language: localStorage.getItem('vivahrang_lang') || 'en',
    pref_age_min: 21, pref_age_max: 35, pref_education: '', pref_religion: '', pref_caste: '', pref_location: '', pref_diet: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const stepIcons = [User, GraduationCap, MapPin, Heart];
  const stepLabels = [t('register.step1'), t('register.step2'), t('register.step3'), t('register.step4')];

  const handleSubmit = async () => {
    if (!form.name || !form.dob || !form.gender) {
      toast.error('Please fill in required fields (Name, DOB, Gender)');
      return;
    }
    setLoading(true);
    try {
      const fbToken = await getFirebaseToken();
      if (!fbToken) {
        toast.error('Session expired. Please login again.');
        navigate('/auth');
        return;
      }
      const profile = { ...form, height: parseInt(form.height) || 0, weight: parseInt(form.weight) || 0,
        pref_age_min: parseInt(form.pref_age_min) || 18, pref_age_max: parseInt(form.pref_age_max) || 40 };
      const res = await api.post('/auth/register', { firebase_token: fbToken, profile });
      // Store backend session token
      if (res.data.token) {
        localStorage.setItem('auth_token', res.data.token);
      }
      setUser(res.data.user);
      setNeedsRegistration(false);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/auth');
      } else {
        toast.error(detail || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, field, type = 'text', placeholder = '') => (
    <div>
      <Label className="text-sm font-medium text-[#2D2424]">{label}</Label>
      <Input
        type={type} value={form[field]} onChange={(e) => update(field, e.target.value)}
        placeholder={placeholder} data-testid={`register-${field}`}
        className="mt-1.5 h-11 rounded-lg border-[#E7E5E4] focus:border-[#D9381E]"
      />
    </div>
  );

  const renderSelect = (label, field, options) => (
    <div>
      <Label className="text-sm font-medium text-[#2D2424]">{label}</Label>
      <Select value={form[field]} onValueChange={(v) => update(field, v)}>
        <SelectTrigger className="mt-1.5 h-11 rounded-lg border-[#E7E5E4]" data-testid={`register-${field}`}>
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const steps = [
    // Step 1: Basic Info
    <div className="space-y-4" key="step1">
      <div>
        <Label className="text-sm font-medium text-[#2D2424]">{t('register.profile_for')}</Label>
        <RadioGroup value={form.profile_for} onValueChange={(v) => update('profile_for', v)} className="mt-2 flex flex-wrap gap-3">
          {['self', 'son', 'daughter', 'sibling', 'relative'].map(opt => (
            <Label key={opt} className={`flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer transition-colors ${
              form.profile_for === opt ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4] hover:border-[#F2A900]'
            }`}>
              <RadioGroupItem value={opt} className="sr-only" />
              {t(`register.${opt}`)}
            </Label>
          ))}
        </RadioGroup>
      </div>
      {renderField(t('register.name'), 'name', 'text', 'Enter full name')}
      <div>
        <Label className="text-sm font-medium text-[#2D2424]">{t('register.gender')}</Label>
        <RadioGroup value={form.gender} onValueChange={(v) => update('gender', v)} className="mt-2 flex gap-4">
          {['male', 'female'].map(g => (
            <Label key={g} className={`flex items-center gap-2 px-6 py-2.5 rounded-full border cursor-pointer transition-colors ${
              form.gender === g ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4] hover:border-[#F2A900]'
            }`}>
              <RadioGroupItem value={g} className="sr-only" />
              {t(`register.${g}`)}
            </Label>
          ))}
        </RadioGroup>
      </div>
      {renderField(t('register.dob'), 'dob', 'date')}
      <div className="grid grid-cols-2 gap-3">
        {renderField('Birth Time', 'birth_time', 'time', 'HH:MM')}
        {renderField('Birth Place', 'birth_place', 'text', 'City of birth')}
      </div>
      {renderSelect(t('register.religion'), 'religion', RELIGIONS)}
      <div className="grid grid-cols-2 gap-3">
        {renderField(t('register.caste'), 'caste')}
        {renderField(t('register.sub_caste'), 'sub_caste')}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {renderField(t('register.height'), 'height', 'number', 'cm')}
        {renderField(t('register.weight'), 'weight', 'number', 'kg')}
        {renderSelect(t('register.complexion'), 'complexion', COMPLEXIONS)}
      </div>
    </div>,

    // Step 2: Education & Career
    <div className="space-y-4" key="step2">
      {renderSelect(t('register.education_degree'), 'education_degree', DEGREES)}
      {renderField(t('register.education_field'), 'education_field', 'text', 'e.g. Computer Science')}
      {renderField(t('register.education_college'), 'education_college', 'text', 'College name')}
      {renderField(t('register.occupation'), 'occupation', 'text', 'e.g. Software Engineer')}
      {renderField(t('register.company'), 'company', 'text', 'Company name')}
      {renderSelect(t('register.income'), 'income', INCOMES)}
      {renderSelect(t('register.family_type'), 'family_type', FAMILY_TYPES)}
      <div className="grid grid-cols-2 gap-3">
        {renderField(t('register.father_occupation'), 'father_occupation')}
        {renderField(t('register.mother_occupation'), 'mother_occupation')}
      </div>
    </div>,

    // Step 3: Lifestyle & Location
    <div className="space-y-4" key="step3">
      {renderSelect(t('register.diet'), 'diet', DIETS)}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-medium">{t('register.smoking')}</Label>
          <RadioGroup value={form.smoking} onValueChange={(v) => update('smoking', v)} className="mt-2 flex gap-3">
            {['no', 'yes'].map(v => (
              <Label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.smoking === v ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4]'}`}>
                <RadioGroupItem value={v} className="sr-only" />{t(`common.${v}`)}
              </Label>
            ))}
          </RadioGroup>
        </div>
        <div>
          <Label className="text-sm font-medium">{t('register.drinking')}</Label>
          <RadioGroup value={form.drinking} onValueChange={(v) => update('drinking', v)} className="mt-2 flex gap-3">
            {['no', 'yes'].map(v => (
              <Label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.drinking === v ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4]'}`}>
                <RadioGroupItem value={v} className="sr-only" />{t(`common.${v}`)}
              </Label>
            ))}
          </RadioGroup>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {renderField(t('register.city'), 'city', 'text', 'Current city')}
        {renderField(t('register.state'), 'state', 'text', 'State')}
      </div>
      {renderField(t('register.native_place'), 'native_place', 'text', 'Native place')}
      {renderField(t('register.hobbies'), 'hobbies', 'text', 'Reading, Music, Travel...')}
      <div>
        <Label className="text-sm font-medium text-[#2D2424]">{t('register.bio')}</Label>
        <Textarea
          value={form.bio} onChange={(e) => update('bio', e.target.value.slice(0, 500))}
          placeholder={t('register.bio_placeholder')} rows={4} maxLength={500}
          className="mt-1.5 rounded-lg border-[#E7E5E4] focus:border-[#D9381E]" data-testid="register-bio"
        />
        <p className="text-xs text-[#78716C] mt-1">{form.bio.length}/500</p>
      </div>
    </div>,

    // Step 4: Partner Preferences
    <div className="space-y-4" key="step4">
      <div className="grid grid-cols-2 gap-3">
        {renderField(t('register.pref_age') + ' (Min)', 'pref_age_min', 'number')}
        {renderField(t('register.pref_age') + ' (Max)', 'pref_age_max', 'number')}
      </div>
      {renderSelect(t('register.pref_education'), 'pref_education', ['Any', ...DEGREES])}
      {renderSelect(t('register.pref_religion'), 'pref_religion', ['Any', ...RELIGIONS])}
      {renderField(t('register.pref_caste'), 'pref_caste', 'text', 'Any / Specific caste')}
      {renderField(t('register.pref_location'), 'pref_location', 'text', 'Any / Specific city')}
      {renderSelect(t('register.pref_diet'), 'pref_diet', ['Any', ...DIETS])}
    </div>,
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] py-8 px-4" data-testid="register-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2D2424]">{t('register.title')}</h1>
          <p className="mt-2 text-sm text-[#78716C]">{t('register.subtitle')}</p>
        </div>

        {/* Step Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {stepLabels.map((label, i) => {
            const Icon = stepIcons[i];
            return (
              <div key={i} className="flex items-center gap-2">
                <button
                  onClick={() => i < step && setStep(i)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    i === step ? 'step-active' : i < step ? 'step-completed' : 'step-pending'
                  }`}
                  data-testid={`step-${i}-btn`}
                >
                  {i < step ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
                {i < 3 && <div className={`w-8 h-0.5 ${i < step ? 'bg-green-500' : 'bg-[#E7E5E4]'}`} />}
              </div>
            );
          })}
        </div>

        <Card className="p-6 sm:p-8 rounded-2xl border border-[#E7E5E4] bg-white shadow-sm">
          {steps[step]}

          <div className="flex justify-between mt-8 pt-6 border-t border-[#E7E5E4]">
            {step > 0 ? (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="rounded-full px-6 border-[#E7E5E4]" data-testid="prev-step-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> {t('register.previous')}
              </Button>
            ) : <div />}
            {step < 3 ? (
              <Button onClick={() => setStep(s => s + 1)} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-6" data-testid="next-step-btn">
                {t('register.next')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-8" data-testid="submit-register-btn">
                {loading ? t('register.creating') : t('register.submit')}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
